import { useNavigate } from 'react-router';
import {
  HoverOverMessage,
  StatusIcon,
  TableOfContentsItem,
  TableOfContentsSection,
} from './TableOfContentsRenderer';
import { useMemo, useState } from 'react';
import DataErrorModal from '../molecules/DataErrorModal';
import { AlertTriangle } from 'lucide-react';
import { TaskStatus } from '../../data/enums';

const TableOfContensItemRenderer = ({
  item,
  section,
  onMultiSelectChange,
}: {
  item: TableOfContentsItem;
  section: TableOfContentsSection;
  onMultiSelectChange?: (item: TableOfContentsItem) => void;
}) => {
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);

  const backgroundColor = useMemo(() => {
    if (item.enabled) {
      return 'bg-white border-line';
    } else if (item.disabledModal) {
      return 'bg-red-50 border-red-200';
    } else {
      return 'bg-gray-50 border-line';
    }
  }, [item.enabled, item.disabledModal]);

  const cursorStyle = useMemo(() => {
    if (item.enabled || item.disabledModal !== undefined) {
      return 'cursor-pointer opacity-100';
    } else {
      return 'cursor-not-allowed opacity-60';
    }
  }, [item.enabled, item.disabledModal]);

  return (
    <>
      <div
        key={item.id}
        onClick={
          item.enabled
            ? () => {
                section.multiselect
                  ? onMultiSelectChange?.(item)
                  : navigate(item.url, {
                      state: {
                        inspectionId: item.inspectionId,
                        inspectionCategoryTypeId: item.inspectionCategoryTypeId,
                        ...item.locationState,
                      },
                    });
              }
            : item.disabledModal !== undefined
              ? () => {
                  setModalOpen(!modalOpen);
                }
              : undefined
        }
        className={`mb-4 flex w-full items-center p-4 last:border-b-0 hover:bg-brandLight/30 ${cursorStyle} ${backgroundColor}`}
      >
        <HoverOverMessage
          text={
            !item.enabled && item.disabledMessage
              ? item.disabledMessage || 'This item is disabled'
              : item.tooltipKey || ''
          }
        >
          <div className="flex w-full items-center">
            <StatusIcon status={item.status} />
            <span
              className={item.enabled ? 'ml-3 text-ink' : 'ml-3 text-inkMuted'}
            >
              {item.label}
            </span>

            {section.multiselect && (
              <input
                type="checkbox"
                className="mr-4 ml-auto size-4 rounded-sm border-line text-brand focus:ring-brand"
                checked={item.multiselected}
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering the parent onClick
                  onMultiSelectChange?.(item);
                }}
                onChange={(e) => {
                  // Handle checkbox change here if needed
                  e.stopPropagation(); // Prevent triggering the parent onClick
                }}
              />
            )}
          </div>
        </HoverOverMessage>

        {item.showErrorIcon && (
          <>
            <AlertTriangle
              color="red"
              className="ml-2 inline-block size-5 text-red-500"
            />
          </>
        )}
      </div>

      <DataErrorModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
        heading={item.disabledModal?.title ?? 'Data Error'}
        message={item.disabledModal?.message}
        children={item.disabledModal?.children}
      />
    </>
  );
};

export default TableOfContensItemRenderer;
